import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { PROTOCOL_VERSION } from "@air-hockey/game-core";
import {
  createPrivateMatch,
  getMatchHistoryEntry,
  joinPrivateMatch,
  submitMatchReport,
  type MatchHistoryEntry,
  type MatchmakingTicket,
  type ReportReason,
} from "./game/api";
import { AudioEngine } from "./game/audioEngine";
import {
  persistActiveMatchResume,
  readActiveMatchResume,
  removeActiveMatchResume,
  type ActiveMatchResume,
} from "./game/activeMatchResume";
import { NetworkSession, type ServerMessage } from "./game/networkSession";
import { OnlineSimulation } from "./game/onlineSimulation";
import { PracticeSimulation } from "./game/practiceSimulation";
import type {
  ConnectionMetrics,
  MatchConnection,
  PlayerInfo,
  RenderState,
  SimulationAdapter,
} from "./game/types";
import { HomeScreen } from "./components/HomeScreen";
import { LobbyScreen, type LobbyPhase } from "./components/LobbyScreen";
import { MatchmakingScreen } from "./components/MatchmakingScreen";
import { PlayerHubScreen } from "./components/PlayerHubScreen";
import { PrivateMatchScreen } from "./components/PrivateMatchScreen";
import { ReportDialog } from "./components/ReportDialog";
import { ResultsScreen } from "./components/ResultsScreen";
import { SettingsPanel } from "./components/SettingsPanel";
import { useDeviceSession } from "./hooks/useDeviceSession";
import { useMatchmaking } from "./hooks/useMatchmaking";
import { usePlayerHub } from "./hooks/usePlayerHub";
import { usePreferences } from "./hooks/usePreferences";
import type { MatchRulesDisplay } from "./components/GameScreen";

const GameScreen = lazy(async () => {
  const module = await import("./components/GameScreen");
  return { default: module.GameScreen };
});

const ReplayScreen = lazy(async () => {
  const module = await import("./components/ReplayScreen");
  return { default: module.ReplayScreen };
});

const WageringScreen = lazy(async () => {
  const module = await import("./components/WageringScreen");
  return { default: module.WageringScreen };
});

type Screen = "home" | "private" | "matchmaking" | "wagering" | "playerHub" | "replay" | "lobby" | "match" | "results";
type PrivateOnlineAction = { mode: "private" } | { mode: "join"; code: string };
type OnlineMode = "quick" | "private" | "join";
type LobbyRetryKind = "private" | "queue" | "resume";

interface LobbyState {
  phase: LobbyPhase;
  mode: OnlineMode;
  joinCode: string | null;
  matchId: string | null;
  error: string | null;
}

interface SessionNames {
  local: string;
  opponent: string;
}

interface ReportEligibility {
  status: "idle" | "checking" | "available" | "unavailable" | "submitted";
  opponentId: string | null;
  message: string | null;
}

const OFFLINE_METRICS: ConnectionMetrics = {
  status: "idle",
  pingMs: null,
  reconnectAttempt: 0,
  message: undefined,
};
const EMPTY_REPORT: ReportEligibility = { status: "idle", opponentId: null, message: null };

export function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const [nickname, setNicknameState] = useState(readNickname);
  const [preferences, updatePreferences] = usePreferences();
  const deviceSession = useDeviceSession(nickname);
  const playerHub = usePlayerHub(deviceSession.session, screen === "playerHub");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [simulation, setSimulation] = useState<SimulationAdapter | null>(null);
  const [metrics, setMetrics] = useState<ConnectionMetrics>(OFFLINE_METRICS);
  const [lobby, setLobby] = useState<LobbyState>({
    phase: "creating",
    mode: "private",
    joinCode: null,
    matchId: null,
    error: null,
  });
  const [sessionNames, setSessionNames] = useState<SessionNames>({ local: nickname, opponent: "Challenger" });
  const [result, setResult] = useState<RenderState | null>(null);
  const [matchRules, setMatchRules] = useState<MatchRulesDisplay>({ mode: "Classic", objective: "First to 7" });
  const [lastOnlineAction, setLastOnlineAction] = useState<PrivateOnlineAction>({ mode: "private" });
  const [lobbyRetryKind, setLobbyRetryKind] = useState<LobbyRetryKind>("private");
  const [currentMatchId, setCurrentMatchId] = useState<string | null>(null);
  const [resumeCandidate, setResumeCandidate] = useState<ActiveMatchResume | null>(readActiveMatchResume);
  const [reportEligibility, setReportEligibility] = useState<ReportEligibility>(EMPTY_REPORT);
  const [reportOpen, setReportOpen] = useState(false);
  const [replayMatch, setReplayMatch] = useState<MatchHistoryEntry | null>(null);
  const networkRef = useRef<NetworkSession | null>(null);
  const simulationRef = useRef<SimulationAdapter | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reportAbortRef = useRef<AbortController | null>(null);
  const subscriptionsRef = useRef<Array<() => void>>([]);
  const requestIdRef = useRef(0);
  const audioRef = useRef<AudioEngine | null>(null);
  audioRef.current ??= new AudioEngine();

  useEffect(() => {
    audioRef.current?.setEnabled(preferences.audio);
  }, [preferences.audio]);

  const clearResume = useCallback(() => {
    removeActiveMatchResume();
    setResumeCandidate(null);
  }, []);

  useEffect(() => {
    if (!resumeCandidate) return;
    const remaining = resumeCandidate.expiresAt - Date.now();
    if (remaining <= 0) {
      clearResume();
      return;
    }
    const timer = window.setTimeout(clearResume, remaining);
    return () => window.clearTimeout(timer);
  }, [clearResume, resumeCandidate]);

  const cleanSession = useCallback((sendForfeit = false) => {
    requestIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    if (sendForfeit) {
      networkRef.current?.forfeit();
      clearResume();
    }
    subscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
    subscriptionsRef.current = [];
    networkRef.current?.close();
    networkRef.current = null;
    simulationRef.current?.dispose();
    simulationRef.current = null;
    setSimulation(null);
    setMetrics(OFFLINE_METRICS);
  }, [clearResume]);

  useEffect(() => {
    return () => {
      cleanSession(false);
      reportAbortRef.current?.abort();
      audioRef.current?.dispose();
    };
  }, [cleanSession]);

  const setNickname = (value: string) => {
    setNicknameState(value);
    try {
      localStorage.setItem("air-hockey.nickname", value);
    } catch {
      // The in-memory name still works when storage is blocked.
    }
  };

  const startPractice = useCallback(() => {
    cleanSession(false);
    reportAbortRef.current?.abort();
    audioRef.current?.unlock();
    const practice = new PracticeSimulation();
    simulationRef.current = practice;
    subscriptionsRef.current.push(practice.subscribe((event) => audioRef.current?.playEvent(event)));
    setSessionNames({ local: safeNickname(nickname), opponent: "Arc Bot" });
    setMatchRules({ mode: "Practice", objective: "2 min · First to 7" });
    setResult(null);
    setCurrentMatchId(null);
    setReportEligibility(EMPTY_REPORT);
    setMetrics(OFFLINE_METRICS);
    setSimulation(practice);
    setScreen("match");
  }, [cleanSession, nickname]);

  const handleLobbyMessage = useCallback((message: ServerMessage, player: PlayerInfo, mode: OnlineMode) => {
    if (message.type === "WELCOME") {
      setMatchRules(readDisplayRules(message));
      setLobby((current) => ({ ...current, phase: "waiting" }));
      return;
    }
    if (message.type === "PLAYER_CONNECTION" && message.connected) {
      const localCoreSlot = player.slot === "A" ? "PLAYER_A" : "PLAYER_B";
      if (message.player !== localCoreSlot) {
        setLobby((current) => ({ ...current, phase: "opponent-ready" }));
      }
      return;
    }
    if (message.type === "COUNTDOWN" || message.type === "GOAL" || message.type === "FINISHED") {
      if (message.type === "FINISHED") clearResume();
      setScreen("match");
      return;
    }
    if (message.type === "SNAPSHOT") {
      if (["countdown", "playing", "goal", "finished"].includes(message.phase)) {
        if (message.phase === "finished") clearResume();
        setScreen("match");
      }
      return;
    }
    if (message.type === "MATCH_STATE" && message.state && typeof message.state === "object") {
      const state = message.state as Record<string, unknown>;
      if (typeof state.phase === "string" && ["countdown", "playing", "goal", "finished"].includes(state.phase)) {
        if (state.phase === "finished") clearResume();
        setScreen("match");
      }
      return;
    }
    if (message.type === "ERROR") {
      setLobby((current) => ({
        ...current,
        phase: "error",
        error: message.message ?? `The ${mode} match could not continue.`,
      }));
    }
  }, [clearResume]);

  const connectOnline = useCallback((connection: MatchConnection, mode: OnlineMode, opponentName: string) => {
    if (connection.protocolVersion !== "unknown" && connection.protocolVersion !== PROTOCOL_VERSION) {
      setLobby((current) => ({
        ...current,
        phase: "error",
        error: `This client expects ${PROTOCOL_VERSION}; the match server reported ${connection.protocolVersion}.`,
      }));
      return;
    }
    const localName = mode === "quick"
      ? deviceSession.session?.player.name ?? safeNickname(nickname)
      : safeNickname(nickname);
    const network = new NetworkSession();
    const online = new OnlineSimulation(network, connection.player.slot);
    networkRef.current = network;
    simulationRef.current = online;
    setCurrentMatchId(connection.matchId);
    setSimulation(online);
    setSessionNames({ local: localName, opponent: opponentName });

    subscriptionsRef.current.push(
      network.subscribeMetrics(setMetrics),
      online.subscribe((event) => audioRef.current?.playEvent(event)),
      network.subscribeMessages((message) => handleLobbyMessage(message, connection.player, mode)),
      network.subscribeResume((credential) => {
        const resume: ActiveMatchResume = {
          version: 1,
          matchId: connection.matchId,
          wsUrl: credential.wsUrl,
          slot: connection.player.slot,
          playerId: connection.player.playerId,
          localName,
          opponentName,
          protocolVersion: credential.protocolVersion,
          physicsVersion: credential.physicsVersion,
          expiresAt: credential.expiresAt,
        };
        persistActiveMatchResume(resume);
        setResumeCandidate(resume);
      }),
    );
    setLobby((current) => ({ ...current, phase: "connecting", error: null }));
    network.connect(connection.player.wsUrl);
  }, [deviceSession.session?.player.name, handleLobbyMessage, nickname]);

  const handleQueueMatch = useCallback((ticket: MatchmakingTicket) => {
    if (!ticket.connection) return;
    cleanSession(false);
    clearResume();
    audioRef.current?.unlock();
    setResult(null);
    setReportEligibility(EMPTY_REPORT);
    setLobbyRetryKind("queue");
    setLobby({
      phase: "connecting",
      mode: "quick",
      joinCode: null,
      matchId: ticket.connection.matchId,
      error: null,
    });
    setScreen("lobby");
    connectOnline(ticket.connection, "quick", ticket.opponent?.name ?? "Matched player");
  }, [cleanSession, clearResume, connectOnline]);

  const matchmaking = useMatchmaking(deviceSession.session, handleQueueMatch);

  const runOnlineAction = useCallback(async (action: PrivateOnlineAction) => {
    cleanSession(networkRef.current !== null);
    clearResume();
    reportAbortRef.current?.abort();
    audioRef.current?.unlock();
    setLastOnlineAction(action);
    setLobbyRetryKind("private");
    setResult(null);
    setReportEligibility(EMPTY_REPORT);
    setLobby({
      phase: "creating",
      mode: action.mode,
      joinCode: action.mode === "join" ? action.code : null,
      matchId: null,
      error: null,
    });
    setScreen("lobby");
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;
    const player = {
      id: deviceSession.session?.player.id ?? deviceSession.deviceId,
      name: safeNickname(nickname),
    };

    try {
      if (action.mode === "join") {
        const connection = await joinPrivateMatch({ joinCode: action.code, player, signal: controller.signal });
        if (requestId !== requestIdRef.current) return;
        setLobby((current) => ({ ...current, matchId: connection.matchId, phase: "connecting" }));
        connectOnline(connection, action.mode, "Host");
      } else {
        const created = await createPrivateMatch({ player, signal: controller.signal });
        if (requestId !== requestIdRef.current) return;
        setLobby((current) => ({
          ...current,
          joinCode: created.joinCode,
          matchId: created.matchId,
          phase: "connecting",
        }));
        connectOnline(
          {
            matchId: created.matchId,
            status: created.status,
            protocolVersion: created.protocolVersion,
            ticketExpiresAt: created.ticketExpiresAt,
            player: created.players.A,
          },
          action.mode,
          created.players.B?.name ?? "Challenger",
        );
      }
    } catch (error) {
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;
      const message = error instanceof Error ? error.message : "The match server could not be reached.";
      setLobby((current) => ({ ...current, phase: "error", error: message }));
    }
  }, [cleanSession, clearResume, connectOnline, deviceSession.deviceId, deviceSession.session, nickname]);

  const resumeMatch = useCallback(() => {
    const resume = readActiveMatchResume();
    if (!resume) {
      clearResume();
      setScreen("home");
      return;
    }
    cleanSession(false);
    reportAbortRef.current?.abort();
    audioRef.current?.unlock();
    setResumeCandidate(resume);
    setResult(null);
    setReportEligibility(EMPTY_REPORT);
    setLobbyRetryKind("resume");
    setLobby({ phase: "connecting", mode: "quick", joinCode: null, matchId: resume.matchId, error: null });
    setScreen("lobby");
    const ticket = new URL(resume.wsUrl).searchParams.get("ticket") ?? "";
    connectOnline({
      matchId: resume.matchId,
      status: "reconnecting",
      protocolVersion: resume.protocolVersion,
      ticketExpiresAt: new Date(resume.expiresAt).toISOString(),
      player: {
        playerId: resume.playerId,
        name: resume.localName,
        slot: resume.slot,
        joinTicket: ticket,
        wsUrl: resume.wsUrl,
      },
    }, "quick", resume.opponentName);
  }, [cleanSession, clearResume, connectOnline]);

  const leaveMatch = () => {
    const online = simulationRef.current?.kind === "online";
    cleanSession(online);
    reportAbortRef.current?.abort();
    setResult(null);
    setCurrentMatchId(null);
    setReportEligibility(EMPTY_REPORT);
    setScreen("home");
  };

  const finishMatch = useCallback((state: RenderState) => {
    const online = simulationRef.current?.kind === "online";
    setResult(state);
    networkRef.current?.close();
    clearResume();
    setScreen("results");
    reportAbortRef.current?.abort();
    if (!online || !currentMatchId || !deviceSession.session) {
      setReportEligibility(online
        ? { status: "unavailable", opponentId: null, message: "Reporting requires an authenticated match record." }
        : EMPTY_REPORT);
      return;
    }
    const controller = new AbortController();
    reportAbortRef.current = controller;
    setReportEligibility({ status: "checking", opponentId: null, message: "Verifying the opponent from your match history…" });
    void getMatchHistoryEntry(deviceSession.session, currentMatchId, controller.signal)
      .then((entry) => {
        if (controller.signal.aborted) return;
        setSessionNames((current) => ({ ...current, opponent: entry.opponentName }));
        setReportEligibility({ status: "available", opponentId: entry.opponentId, message: null });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setReportEligibility({
          status: "unavailable",
          opponentId: null,
          message: "The match is not yet available in authenticated history, so reporting is disabled.",
        });
      });
  }, [clearResume, currentMatchId, deviceSession.session]);

  const submitReport = useCallback(async (reason: ReportReason, details: string, idempotencyKey: string) => {
    if (!deviceSession.session || !currentMatchId || !reportEligibility.opponentId) {
      throw new Error("This match is not eligible for a report.");
    }
    await submitMatchReport({
      matchId: currentMatchId,
      reportedUserId: reportEligibility.opponentId,
      reason,
      details,
      session: deviceSession.session,
      idempotencyKey,
    });
    setReportEligibility((current) => ({ ...current, status: "submitted", message: "Report submitted." }));
  }, [currentMatchId, deviceSession.session, reportEligibility.opponentId]);

  const leaveMatchmaking = () => {
    void matchmaking.cancel();
    setScreen("home");
  };

  const retryLobby = () => {
    if (lobbyRetryKind === "queue") {
      cleanSession(networkRef.current !== null);
      matchmaking.reset();
      setScreen("matchmaking");
    } else if (lobbyRetryKind === "resume") {
      resumeMatch();
    } else {
      void runOnlineAction(lastOnlineAction);
    }
  };

  const rootClass = [
    "app-shell",
    preferences.highContrast ? "is-high-contrast" : "",
    preferences.reducedMotion ? "is-reduced-motion" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      <div className="ambient ambient--cyan" aria-hidden="true" />
      <div className="ambient ambient--violet" aria-hidden="true" />

      {screen === "home" && (
        <HomeScreen
          nickname={nickname}
          onNicknameChange={setNickname}
          onQuickMatch={() => {
            matchmaking.reset();
            setScreen("matchmaking");
          }}
          onPrivateMatch={() => setScreen("private")}
          onPractice={startPractice}
          onWagering={() => setScreen("wagering")}
          onPlayerHub={() => setScreen("playerHub")}
          onSettings={() => setSettingsOpen(true)}
          onlineStatus={deviceSession.status}
          resumeAvailable={resumeCandidate !== null}
          onResume={resumeMatch}
        />
      )}
      {screen === "wagering" && (
        <Suspense fallback={<div className="loading-screen" role="status"><span className="spinner" /> Preparing stake queue…</div>}>
          <WageringScreen onBack={() => setScreen("home")} session={deviceSession.session} sessionStatus={deviceSession.status} />
        </Suspense>
      )}
      {screen === "matchmaking" && (
        <MatchmakingScreen
          controller={matchmaking}
          sessionStatus={deviceSession.status}
          sessionError={deviceSession.error}
          supportedModes={deviceSession.session?.capabilities.matchmakingModes ?? ["casual", "ranked"]}
          onBack={leaveMatchmaking}
          onPrivate={() => {
            void matchmaking.cancel();
            setScreen("private");
          }}
          onPractice={() => {
            void matchmaking.cancel();
            startPractice();
          }}
        />
      )}
      {screen === "playerHub" && (
        <PlayerHubScreen
          data={playerHub}
          playerId={deviceSession.session?.player.id ?? null}
          onBack={() => setScreen("home")}
          onReplay={(match) => {
            setReplayMatch(match);
            setScreen("replay");
          }}
        />
      )}
      {screen === "replay" && replayMatch && deviceSession.session && (
        <Suspense fallback={<div className="loading-screen" role="status"><span className="spinner" /> Preparing replay viewer…</div>}>
          <ReplayScreen
            session={deviceSession.session}
            match={replayMatch}
            preferences={preferences}
            onBack={() => setScreen("playerHub")}
          />
        </Suspense>
      )}
      {screen === "replay" && (!replayMatch || !deviceSession.session) && (
        <main className="loading-screen" role="alert">
          <p>An authenticated history session is required to open a replay.</p>
          <button className="secondary-button" type="button" onClick={() => setScreen("playerHub")}>Back to history</button>
        </main>
      )}
      {screen === "private" && (
        <PrivateMatchScreen
          onBack={() => setScreen("home")}
          onCreate={() => void runOnlineAction({ mode: "private" })}
          onJoin={(code) => void runOnlineAction({ mode: "join", code })}
        />
      )}
      {screen === "lobby" && (
        <LobbyScreen
          {...lobby}
          metrics={metrics}
          onCancel={() => {
            cleanSession(networkRef.current !== null);
            setScreen("home");
          }}
          onRetry={retryLobby}
        />
      )}
      {screen === "match" && simulation && (
        <Suspense fallback={<div className="loading-screen" role="status"><span className="spinner" /> Preparing the table…</div>}>
          <GameScreen
            simulation={simulation}
            localName={sessionNames.local}
            opponentName={sessionNames.opponent}
            preferences={preferences}
            metrics={metrics}
            rules={matchRules}
            onSettings={() => setSettingsOpen(true)}
            onLeave={leaveMatch}
            onFinished={finishMatch}
            onInteract={() => audioRef.current?.unlock()}
            onCountdown={(value) => audioRef.current?.playCountdown(value)}
          />
        </Suspense>
      )}
      {screen === "results" && result && (
        <ResultsScreen
          state={result}
          practice={simulation?.kind === "practice"}
          localName={sessionNames.local}
          opponentName={sessionNames.opponent}
          reportAvailable={reportEligibility.status === "available"}
          reportMessage={reportEligibility.message}
          {...(simulation?.kind === "online" ? { onReport: () => setReportOpen(true) } : {})}
          onPlayAgain={startPractice}
          onHome={() => {
            cleanSession(false);
            reportAbortRef.current?.abort();
            setResult(null);
            setCurrentMatchId(null);
            setReportEligibility(EMPTY_REPORT);
            setScreen("home");
          }}
        />
      )}

      {reportOpen && (
        <ReportDialog opponentName={sessionNames.opponent} onSubmit={submitReport} onClose={() => setReportOpen(false)} />
      )}
      {settingsOpen && (
        <SettingsPanel preferences={preferences} onChange={updatePreferences} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

function readNickname(): string {
  try {
    return localStorage.getItem("air-hockey.nickname") || "Player One";
  } catch {
    return "Player One";
  }
}

function safeNickname(nickname: string): string {
  return nickname.trim() || "Player One";
}

function readDisplayRules(message: ServerMessage): MatchRulesDisplay {
  if (message.type !== "WELCOME" || message.rules === null || typeof message.rules !== "object") {
    return { mode: "Classic", objective: "First to 7" };
  }
  const rules = message.rules as Record<string, unknown>;
  const points = typeof rules.pointsToWin === "number" ? rules.pointsToWin : 7;
  if (rules.mode === "timed") return { mode: "Timed", objective: `${points} goal cap` };
  if (rules.mode === "sudden-death") return { mode: "Sudden death", objective: "First goal wins" };
  return { mode: "Classic", objective: `First to ${points}` };
}
